import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Minus, ShoppingBag, ArrowLeft, Package, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { imgSrc } from "../lib/imgSrc";
import { registerQrHitFromUrl, qrAttributionFields, clearQrAttribution } from "../lib/qrAttribution";
import StripePaymentForm from "../components/StripePaymentForm";
import { CustomerAddressFields, emptyAddress, hasAddress } from "../components/CustomerAddressFields";
import { OfflinePaymentOptions, isOfflinePayment } from "../components/OfflinePaymentOptions";
import { formatApiError } from "../lib/errors";

/**
 * ExpressCheckout — `/buy/:id`.
 *
 * Lean single-page purchase flow reachable via QR from an in-store display.
 * Shows the product, a qty stepper, name/email, and immediately-usable
 * pay controls. No cart or shipping — customer address is still required;
 * because the buyer is standing next to the item.
 *
 * Payment options:
 *   • Cash on Pickup (default primary CTA, matches the walk-up scenario)
 *   • Card via Stripe (fallback / for those who prefer not to carry cash)
 *
 * On success we hop to /account/orders so the buyer sees the confirmation
 * and pickup instructions.
 */
const currency = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const ExpressCheckout = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [qty, setQty] = useState(1);
    const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", ...emptyAddress });
    const [offline, setOffline] = useState({ method: "cash", delivery: "drop_off" });
    const [submitError, setSubmitError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [placed, setPlaced] = useState(null);

    useEffect(() => {
        api.get(`/products/${id}`)
            .then((r) => setProduct(r.data))
            .catch(() => setProduct(null))
            .finally(() => setLoading(false));
        // Fire the analytics hit + stash attribution (60-min window).
        registerQrHitFromUrl("product", id);
    }, [id]);

    const price = Number(product?.price || 0);
    const total = useMemo(() => Math.round(price * qty * 100) / 100, [price, qty]);
    const stock = Number(product?.stock || 0);
    const outOfStock = product && stock <= 0;
    const overStock = qty > stock;

    const canSubmit = product && !outOfStock && !overStock && form.customer_name.trim() && form.customer_email.trim().includes("@") && hasAddress(form) && qty >= 1;

    /**
     * Build the order body once so both the COD path and the Stripe path
     * post an identical order — differing only in payment_method.
     */
    const buildOrderBody = (paymentMethod) => ({
        items: [{
            product_id: product.id,
            name: product.name,
            price,
            quantity: qty,
            image_url: (Array.isArray(product.images) && product.images[0])
                ? (typeof product.images[0] === "string" ? product.images[0] : (product.images[0]?.url || ""))
                : (product.image_url || ""),
        }],
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim(),
        customer_phone: form.customer_phone.trim(),
        shipping_address: form.shipping_address.trim(),
        shipping_city: form.shipping_city.trim(),
        shipping_state: form.shipping_state.trim(),
        shipping_zip: form.shipping_zip.trim(),
        fulfillment_type: "in_person_pickup",
        payment_method: paymentMethod,
        payment_delivery: isOfflinePayment(paymentMethod) ? offline.delivery : null,
        ...qrAttributionFields(product.id),
    });

    const placeCodOrder = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        setSubmitError("");
        try {
            const { data } = await api.post("/orders", buildOrderBody(offline.method));
            clearQrAttribution();
            setPlaced({ ...data, method: offline.method });
        } catch (e) {
            setSubmitError(formatApiError(e, "Couldn't place order — please try again"));
        } finally {
            setSubmitting(false);
        }
    };

    /** Stripe path: create a pending order, hand id to StripePaymentForm. */
    const createStripePending = async () => {
        const { data } = await api.post("/orders", buildOrderBody("stripe"));
        return data.id;
    };

    const onStripeSuccess = async (order) => {
        clearQrAttribution();
        setPlaced({ ...order, method: "stripe" });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#FAF9F5] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-[#4A5568]" />
            </div>
        );
    }
    if (!product) {
        return (
            <div className="min-h-screen bg-[#FAF9F5] flex flex-col items-center justify-center p-6" data-testid="express-checkout-not-found">
                <Package className="w-12 h-12 text-[#4A5568] mb-4" strokeWidth={1.25} />
                <h1 className="font-heading text-2xl text-[#1C252E] mb-2">Product not available</h1>
                <p className="text-sm text-[#4A5568] mb-6">This QR may be out of date. Browse the shop or scan a fresher code.</p>
                <button
                    onClick={() => navigate("/shop")}
                    className="px-5 py-2.5 rounded-full bg-[#E53935] text-white text-sm font-medium hover:bg-[#c62828]"
                >
                    Visit the shop
                </button>
            </div>
        );
    }

    if (placed) {
        const isCod = isOfflinePayment(placed.method);
        return (
            <div className="min-h-screen bg-[#FAF9F5] flex items-center justify-center p-6" data-testid="express-checkout-success">
                <div className="max-w-md w-full bg-white border border-black/10 rounded-2xl p-8 text-center">
                    <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-7 h-7 text-emerald-700" strokeWidth={1.75} />
                    </div>
                    <h1 className="font-heading text-2xl text-[#1C252E] font-medium mb-1">
                        {isCod ? "Order confirmed!" : "Order placed"}
                    </h1>
                    <p className="text-sm text-[#4A5568] mb-4">
                        {isCod
                            ? "Your order is saved. Your cash or check will be confirmed when it arrives."
                            : "Card charged. You can pick up any time."}
                    </p>
                    <p className="text-xs text-[#4A5568] mb-1">Order</p>
                    <p className="font-mono text-sm text-[#1C252E] mb-4">{placed.order_number}</p>
                    {isCod ? (
                        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 mb-5 text-left" data-testid="express-cod-amount-due">
                            <p data-testid="express-offline-status" className="text-[10px] uppercase tracking-[0.14em] font-bold text-amber-800 mb-1">Awaiting {placed.payment_method} confirmation</p>
                            <p className="font-heading text-3xl text-amber-900 font-semibold">{currency(placed.total || total)}</p>
                            <p className="text-xs text-amber-800 mt-2">
                                Cash or check made out to <strong>Reddog Community Foundation Inc.</strong>. Mail checks to
                                <br /><span className="font-mono">PO Box 231, Frenchtown, MT 59834</span>
                                <br />or call <a href="tel:+14068020279" className="underline font-semibold">(406) 802-0279</a> to arrange pickup.
                                <br />We&apos;ll hold your order for 30 days.
                            </p>
                        </div>
                    ) : (
                        <p className="font-heading text-3xl text-[#E53935] font-medium mb-4">{currency(placed.total || total)}</p>
                    )}
                    <p className="text-xs text-[#4A5568] mb-6">
                        A confirmation email is on its way to <strong className="text-[#1C252E]">{form.customer_email}</strong>.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2 justify-center">
                        <button
                            onClick={() => navigate("/account/orders")}
                            data-testid="express-view-orders"
                            className="px-5 py-2.5 rounded-full bg-[#E53935] text-white text-sm font-medium hover:bg-[#c62828]"
                        >
                            View order
                        </button>
                        <button
                            onClick={() => navigate("/shop")}
                            className="px-5 py-2.5 rounded-full border border-black/15 text-[#1C252E] text-sm font-medium hover:bg-[#F4F1EA]"
                        >
                            Browse shop
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const firstImg = (Array.isArray(product.images) && product.images[0])
        ? (typeof product.images[0] === "string" ? product.images[0] : (product.images[0]?.url || ""))
        : (product.image_url || "");

    return (
        <div className="min-h-screen bg-[#FAF9F5]" data-testid="express-checkout-page">
            {/* Slim top bar */}
            <div className="border-b border-black/5 bg-white">
                <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
                    <button
                        onClick={() => navigate("/shop")}
                        className="flex items-center gap-1.5 text-xs text-[#4A5568] hover:text-[#1C252E]"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" /> Browse shop
                    </button>
                    <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#E53935]">
                        Express checkout · scanned in-store
                    </p>
                </div>
            </div>

            <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-5">
                {/* Product hero */}
                <div className="bg-white border border-black/10 rounded-2xl overflow-hidden">
                    <div className="flex flex-col sm:flex-row">
                        <div className="w-full sm:w-56 h-52 sm:h-auto bg-[#F4F1EA] flex-shrink-0">
                            {firstImg ? (
                                <img src={imgSrc(firstImg, 480, 80)} alt={product.name} className="w-full h-full object-cover" data-testid="express-product-image" />
                            ) : null}
                        </div>
                        <div className="p-5 sm:p-6 flex-1 min-w-0">
                            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#E53935] mb-1">
                                {product.category}
                            </p>
                            <h1 className="font-heading text-2xl sm:text-3xl text-[#1C252E] font-medium leading-tight" data-testid="express-product-name">
                                {product.name}
                            </h1>
                            {product.description ? (
                                <p className="text-sm text-[#4A5568] mt-2 line-clamp-2">{product.description}</p>
                            ) : null}
                            <div className="mt-4 flex items-center justify-between">
                                <p className="font-heading text-3xl text-[#1C252E] font-medium" data-testid="express-product-price">
                                    {currency(price)}
                                </p>
                                {outOfStock ? (
                                    <span className="px-3 py-1 rounded-full bg-red-100 text-red-800 text-xs font-medium">Out of stock</span>
                                ) : (
                                    <span className="text-xs text-[#4A5568]">{stock} available</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Qty + contact */}
                <div className="bg-white border border-black/10 rounded-2xl p-5 sm:p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <label className="text-xs font-medium uppercase tracking-wider text-[#4A5568]">Quantity</label>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => setQty((v) => Math.max(1, v - 1))}
                                disabled={qty <= 1}
                                data-testid="express-qty-dec"
                                className="w-9 h-9 rounded-lg border border-black/10 flex items-center justify-center hover:bg-[#F4F1EA] disabled:opacity-30"
                                aria-label="Decrease quantity"
                            >
                                <Minus className="w-4 h-4" />
                            </button>
                            <input
                                type="number"
                                min="1"
                                value={qty}
                                onChange={(e) => setQty(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
                                data-testid="express-qty-input"
                                className="w-14 h-9 text-center text-sm border border-black/15 rounded-lg focus:outline-none focus:border-[#E53935]"
                            />
                            <button
                                type="button"
                                onClick={() => setQty((v) => v + 1)}
                                disabled={qty >= stock}
                                data-testid="express-qty-inc"
                                className="w-9 h-9 rounded-lg border border-black/10 flex items-center justify-center hover:bg-[#F4F1EA] disabled:opacity-30"
                                aria-label="Increase quantity"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    {overStock ? (
                        <p className="text-xs text-red-700">Only {stock} left — please reduce the quantity.</p>
                    ) : null}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">Your name</label>
                            <input
                                type="text"
                                value={form.customer_name}
                                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                                data-testid="express-name"
                                placeholder="First & last"
                                className="w-full px-3 py-2 border border-black/15 rounded-lg text-sm focus:outline-none focus:border-[#E53935]"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">Email <span className="text-[#4A5568]/60 font-normal normal-case tracking-normal">(receipt)</span></label>
                            <input
                                type="email"
                                value={form.customer_email}
                                onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                                data-testid="express-email"
                                placeholder="you@example.com"
                                className="w-full px-3 py-2 border border-black/15 rounded-lg text-sm focus:outline-none focus:border-[#E53935]"
                                required
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">Phone <span className="text-[#4A5568]/60 font-normal normal-case tracking-normal">(optional)</span></label>
                            <input
                                type="tel"
                                value={form.customer_phone}
                                onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                                data-testid="express-phone"
                                placeholder="(406) 555-0100"
                                className="w-full px-3 py-2 border border-black/15 rounded-lg text-sm focus:outline-none focus:border-[#E53935]"
                            />
                        </div>
                    </div>
                    <CustomerAddressFields value={form} onChange={setForm} prefix="express" />
                </div>

                {/* Total + pay */}
                <div className="bg-white border border-black/10 rounded-2xl p-5 sm:p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-[#4A5568]">Total</span>
                        <span className="font-heading text-3xl text-[#1C252E] font-medium" data-testid="express-total">{currency(total)}</span>
                    </div>

                    {/* Primary CTA: Cash on Pickup */}
                    <OfflinePaymentOptions prefix="express-offline" method={offline.method} delivery={offline.delivery} onChange={(method, delivery) => setOffline({ method, delivery })} disabled={submitting} />
                    {submitError && <p data-testid="express-submit-error" role="alert" className="text-sm text-red-700">{submitError}</p>}
                    <button
                        type="button"
                        onClick={placeCodOrder}
                        disabled={!canSubmit || submitting}
                        data-testid="express-cod-submit"
                        className="w-full py-3.5 rounded-full bg-[#E53935] text-white font-medium hover:bg-[#c62828] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
                    >
                        {submitting ? (
                            <><Loader2 className="w-4 h-4 animate-spin" /> Reserving…</>
                        ) : (
                            <><ShoppingBag className="w-4 h-4" /> Reserve · pay by {offline.method} · {currency(total)}</>
                        )}
                    </button>
                    <p className="text-[11px] text-[#4A5568] text-center -mt-2">
                        Reserves your item. Pay with cash on arrival, or mail a check payable to <strong>Reddog Community Foundation Inc.</strong> to <span className="font-mono">PO Box 231, Frenchtown, MT 59834</span>.
                    </p>

                    {/* Divider */}
                    {total > 0 && canSubmit && (
                        <>
                            <div className="flex items-center gap-3 pt-1">
                                <div className="flex-1 h-px bg-black/10" />
                                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">Or pay with card</p>
                                <div className="flex-1 h-px bg-black/10" />
                            </div>
                            <StripePaymentForm
                                purpose="shop"
                                amount={total}
                                getCartId={createStripePending}
                                donorName={form.customer_name}
                                donorEmail={form.customer_email}
                                onSuccess={onStripeSuccess}
                            />
                        </>
                    )}
                    {!canSubmit && (
                        <p data-testid="express-required-fields-note" className="text-[11px] text-[#4A5568] text-center">Name, email and customer address required.</p>
                    )}
                </div>

                <p className="text-[11px] text-[#4A5568] text-center pb-6">
                    100% of the margin supports approved programs of Reddog Community Foundation Inc.
                </p>
            </div>
        </div>
    );
};

export default ExpressCheckout;
